import React from 'react';
import { Monitor } from 'lucide-react';

/**
 * DevOnlyGuard - Shows info banner when dev-only pages are accessed outside dev VMs.
 * Used as an early return in development pages, so the whole section is blocked rather
 * than the controls being left reachable alongside a warning.
 *
 * `message` overrides the default explanation. The original callers are pages that read
 * local files; the sync tools are dev-only for a different reason (the cross-environment
 * connection strings live only in appsettings.Development.json), and saying "reads local
 * files" there would be wrong.
 */
const DevOnlyGuard = ({ pageName, message }) => {
  return (
    <div className="stui-module-loading">
      <div className="stui-auth-gate-card">
        <div className="stui-auth-gate-body">
          <div className="stui-auth-gate-deadend">
            <Monitor size={48} className="stui-text-muted" />
            <div className="stui-auth-gate-deadend-title">
              {pageName || 'Development Tool'} — Dev VMs Only
            </div>
            <div className="stui-auth-gate-deadend-text">
              {message ||
                'This page reads local files that are only available on development VMs. ' +
                'It is not accessible in the Azure environment.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * HOC wrapper — gates a component behind IS_DEV_VM check.
 * Usage: export default withDevGuard(MyPage, 'My Page');
 */
export const withDevGuard = (Component, pageName) => {
  return function DevGuardedComponent(props) {
    if (!window.__config__?.IS_DEV_VM) return <DevOnlyGuard pageName={pageName} />;
    return <Component {...props} />;
  };
};

export default DevOnlyGuard;
